import { cn } from "@/lib/utils";
import { Z_INDEX } from "@/lib/zIndex";
import {
    defaultDropAnimationSideEffects,
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    DropAnimation,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { RefObject, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import MultipleEmailAddressInput from "./MultipleEmailAddressInput";
import { RecipientDragData } from "./RecipientChip";
import RecipientDragPreview, {
    RecipientDropState,
} from "./RecipientDragPreview";
import {
    getRecipientFields,
    moveRecipient,
    RecipientField,
    RecipientsByField,
} from "./recipients";

type MailDestinationInputsProps = {
    toRecipientsState: [string[], (toRecipients: string[]) => void];
    ccRecipientsState: [string[], (ccRecipients: string[]) => void];
    bccRecipientsState: [string[], (cciRecipients: string[]) => void];
    toInputRef?: RefObject<HTMLInputElement | null>;
    className?: string;
};

const DRAG_ACTIVATION_DISTANCE = 5;

const RETURN_DROP_ANIMATION: DropAnimation = {
    duration: 280,
    easing: "cubic-bezier(0.2, 0.9, 0.3, 1.2)",
    sideEffects: defaultDropAnimationSideEffects({
        styles: { active: { opacity: "0.4" } },
    }),
};

const MailDestinationInputs = ({
    toRecipientsState,
    ccRecipientsState,
    bccRecipientsState,
    toInputRef,
    className,
}: MailDestinationInputsProps) => {
    const { t } = useTranslation();

    const [isSelected, setIsSelected] = useState(false);
    const [draggedRecipient, setDraggedRecipient] =
        useState<RecipientDragData | null>(null);
    const [overField, setOverField] = useState<RecipientField | null>(null);
    const [isDropAccepted, setIsDropAccepted] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
        }),
    );

    const fields: Record<
        RecipientField,
        [string[], (recipients: string[]) => void]
    > = {
        to: toRecipientsState,
        cc: ccRecipientsState,
        bcc: bccRecipientsState,
    };

    const recipients: RecipientsByField = {
        to: toRecipientsState[0],
        cc: ccRecipientsState[0],
        bcc: bccRecipientsState[0],
    };

    const resetDrag = () => {
        setDraggedRecipient(null);
        setOverField(null);
    };

    const handleDragStart = ({ active }: DragStartEvent) => {
        setDraggedRecipient(active.data.current as RecipientDragData);
        setIsDropAccepted(false);
    };

    const handleDragOver = ({ over }: DragOverEvent) => {
        setOverField((over?.id as RecipientField | undefined) ?? null);
    };

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        resetDrag();
        if (!over) {
            return;
        }

        const { email, field: source } = active.data
            .current as RecipientDragData;
        const target = over.id as RecipientField;
        const next = moveRecipient(recipients, email, source, target);

        if (next === recipients) {
            return;
        }
        setIsDropAccepted(true);
        fields[source][1](next[source]);
        fields[target][1](next[target]);
    };

    const labels: Record<RecipientField, string> = {
        to: t("email.to"),
        cc: t("email.cc"),
        bcc: t("email.bcc"),
    };

    const getAlreadyPresentReason = (email: string) => {
        const presentIn = getRecipientFields(recipients, email);

        return presentIn.length > 0
            ? t("email.recipientAlreadyPresentIn", {
                  fields: presentIn.map((field) => labels[field]).join(", "),
              })
            : undefined;
    };

    const getDropState = (): RecipientDropState => {
        if (overField === null) {
            return "forbidden";
        }

        return overField === draggedRecipient?.field ? "idle" : "accepted";
    };
    const dropState = getDropState();
    const isDragging = draggedRecipient !== null;

    return (
        <DndContext
            onDragCancel={resetDrag}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart}
            sensors={sensors}
        >
            <div
                className={cn(
                    "flex flex-col pr-10 transition-all duration-300 h-fit",
                    className,
                )}
                onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setIsSelected(false);
                    }
                }}
                onClick={() => setIsSelected(true)}
                onFocus={() => setIsSelected(true)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        setIsSelected(true);
                    }
                }}
                role="button"
                tabIndex={0}
            >
                <MultipleEmailAddressInput
                    id="to-input"
                    field="to"
                    focus={isSelected || isDragging}
                    getUnavailableReason={getAlreadyPresentReason}
                    label={labels.to}
                    setToRecipients={toRecipientsState[1]}
                    toInputRef={toInputRef}
                    toRecipients={toRecipientsState[0]}
                    alwaysVisible
                />
                <MultipleEmailAddressInput
                    field="cc"
                    focus={isSelected || isDragging}
                    getUnavailableReason={getAlreadyPresentReason}
                    label={labels.cc}
                    setToRecipients={ccRecipientsState[1]}
                    toRecipients={ccRecipientsState[0]}
                />
                <MultipleEmailAddressInput
                    field="bcc"
                    focus={isSelected || isDragging}
                    getUnavailableReason={getAlreadyPresentReason}
                    label={labels.bcc}
                    setToRecipients={bccRecipientsState[1]}
                    toRecipients={bccRecipientsState[0]}
                />
            </div>
            {createPortal(
                <DragOverlay
                    className={
                        dropState === "forbidden"
                            ? "cursor-not-allowed"
                            : "cursor-grabbing"
                    }
                    dropAnimation={
                        isDropAccepted ? null : RETURN_DROP_ANIMATION
                    }
                    zIndex={Z_INDEX.toast}
                >
                    {draggedRecipient && (
                        <RecipientDragPreview
                            dropState={dropState}
                            email={draggedRecipient.email}
                            targetLabel={
                                overField ? labels[overField] : undefined
                            }
                        />
                    )}
                </DragOverlay>,
                document.body,
            )}
        </DndContext>
    );
};

export default MailDestinationInputs;
